import { LoaderFunction } from 'react-router-dom';

// Sentry
import * as Sentry from '@sentry/react';
import debug from 'debug';

// Types
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';
import { AuthContextType } from '../../../../../hooks/useAuth';

// Constants
import {
  formActionNaming,
  formActionValidationNaming,
} from '../../../../../components/forms/ActionForms/constants';
import { externalCompaniesNaming } from '../../../../../components/display/tables/formTables/ExternalCompaniesSelectionTable/constants';

// Selector
import {
  selectActiveOperatorsAndVehicles,
  selectOperatorsAndVehiclesFormValues,
} from '../../../../../gql/selectors/actions';

// Static data
import { hoursType } from '../../../../../data/orders';

// GQL
import { GET_EXTERNAL_COMPANIES } from '../../../../../gql/queries/actions/externalCompanies';
import { GET_ACTION_BY_ID } from '../../../../../gql/queries/actions/actions';
import { client } from '../../../../../gql/client';
import {
  GET_CONCESSIONS,
  GET_ORDER_BY_ID,
} from '../../../../../gql/queries/orders/orders';
import { reconcileValidationActionContext } from '../../../../../utils/validationActionContext';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import { ACTION_MEDIA_SCOPE } from '../../../../../utils/draftScopes';

// Configure debug namespaces
const logLoader = debug('rois:operators:loader:validation');
const logData = debug('rois:operators:data:validation');
const logDuplicate = debug('rois:operators:duplicate:validation');
const logError = debug('rois:operators:error:validation');
const logDebug = debug('rois:action:validation:debug');

// 🎯 DEBUG ESPECÍFICO: Solo operarios y driver (JSON.stringify para copiar fácilmente)
const logOpsDriver = debug('rois:debug:ops-driver');

const loadFormOptions = async (
  userData: AuthContextType | null,
  actionId: string
) => {
  logLoader('⚙️ loadFormOptions INICIADO - actionId:', actionId);

  try {
    if (userData?.user) {
      logLoader('⚙️ Usuario autenticado, procediendo con la carga...');

      // 1. Cargar operarios activos
      const internalOperators =
        (await selectActiveOperatorsAndVehicles(false)) ?? [];
      logData('⚙️ selectActiveOperatorsAndVehicles:', (internalOperators as any)?.operators?.length || 0, 'operarios disponibles');

      // 2. Cargar valores del formulario (CRÍTICO - aquí se obtienen operarios/vehículos asignados)
      logLoader('⚙️ Llamando a selectOperatorsAndVehiclesFormValues...');
      const formValues = await selectOperatorsAndVehiclesFormValues(actionId);
      logLoader('⚙️ selectOperatorsAndVehiclesFormValues completado');
      logData('⚙️ formValues recibidos:');
      logData('    - operatorsTable:', formValues?.operatorsTable?.length || 0);
      logData('    - vehicleTable keys:', Object.keys(formValues?.vehicleTable || {}).length);
      logData('    - externalWorks:', formValues?.externalWorks?.length || 0);
      logData('    - hoursType:', formValues?.hoursType);
      logData('    - isExternal:', formValues?.isExternal);

      // Detalle de operarios del formValues
      if (formValues?.operatorsTable?.length > 0) {
        logData('⚙️ formValues.operatorsTable detalle:');
        formValues.operatorsTable.forEach((op: any, idx: number) => {
          if (op) {
            logData(`      [${idx}] ${op?.name || 'N/A'} ${op?.surName || ''} - internal: ${op?.internal}`);
          }
        });
      } else {
        logError('⚠️ formValues.operatorsTable está VACÍO');
      }

      // 3. Cargar empresas externas
      const { readExternalCompanies } = client.readQuery({
        query: GET_EXTERNAL_COMPANIES,
      });
      logData('⚙️ readExternalCompanies:', readExternalCompanies?.length || 0, 'empresas');

      const sortedExternalCompanies = [...readExternalCompanies].sort((a, b) =>
        a.companyName.localeCompare(b.companyName)
      );

      const result = {
        internalOperators,
        externalCompanies: sortedExternalCompanies,
        hoursTypeOptions: hoursType,
        flowData: 'validation',
        ...formValues,
        externals: [...formValues.externalWorks],
      };

      logLoader('⚙️ loadFormOptions COMPLETADO - retornando datos');
      return result;
    } else {
      logError('⚠️ loadFormOptions - userData.user no existe, retornando undefined');
    }
  } catch (err) {
    Sentry.captureException(err);
    logError('❌ Operators and vehicles loading error:', err);
    return true;
  }
};

export const OperatorsAndVehiclesValidationLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined = (userData) => async ({ params }) => {
  // La URL manda: si el contexto de la pestaña es de OTRA actuación se borra
  // entero y esta carga sale por el `:id` de la URL. Síncrono y antes de
  // cualquier `await` (diagnóstico 2026-10-08 § 8).
  reconcileValidationActionContext(params.id);
  // La lista de fotos y el croquis en curso también son de la actuación
  // de la URL. Las carpetas de OPFS no se tocan: las comparten las
  // pestañas (spec 2026-11-02 wizard-draft-ownership § 6).
  claimDraftScope(ACTION_MEDIA_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Enable logs only in development
  if (import.meta.env.DEV) {
    debug.enable('rois:debug:ops-driver');

    logOpsDriver('════════════════════════════════════════════════════════════════');
    logOpsDriver('🎯 LOADER VALIDATION - INICIO');
    logOpsDriver('actionId: ' + (params?.id || 'NO DEFINIDO'));
    logOpsDriver('════════════════════════════════════════════════════════════════');
  }

  const stringifyForm = sessionStorage.getItem(
    formActionValidationNaming.NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM
  );

  const stringifyActionData = sessionStorage.getItem(
    formActionNaming.NEW_VALIDATION_ACTION_DATA
  );

  let sessionStorageData;
  let actionData;
  let parentExpeditionOrderStatus;
  let state;

  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 📦 DEBUG: SessionStorage Data Leída
  // ═══════════════════════════════════════════════════════════════════════════
  logLoader('📦 SessionStorage verificación:');
  logLoader('  - NEW_VALIDATION_OPERATORS_AND_VEHICLE_FORM existe:', !!stringifyForm);
  logLoader('  - NEW_VALIDATION_ACTION_DATA existe:', !!stringifyActionData);
  if (sessionStorageData) {
    logData('📦 SessionStorage - datos parseados:');
    logData('  - operatorsTable:', sessionStorageData?.operatorsTable?.length || 0, 'elementos');
    logData('  - vehicleTable keys:', Object.keys(sessionStorageData?.vehicleTable || {}).length);
    logData('  - externals:', sessionStorageData?.externals?.length || 0);
    logData('  - hoursType:', sessionStorageData?.hoursType);
    logData('  - isExternal:', sessionStorageData?.isExternal);
    if (sessionStorageData?.operatorsTable?.length > 0) {
      sessionStorageData.operatorsTable.forEach((op: any, idx: number) => {
        if (op) {
          logData(`    [${idx}] ${op?.name || op?.operator?.name || 'SIN NOMBRE'} - internal: ${op?.internal}`);
        }
      });
    }
  } else {
    logLoader('📦 SessionStorage - SIN DATOS (sessionStorageData es null/undefined)');
  }

  if (stringifyActionData) {
    actionData = JSON.parse(decompressSessionStorage(stringifyActionData));
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: actionData.orderId,
      },
    });
    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  } else if (params.id) {
    // BUG #5 FIX: When navigating via double-click, sessionStorage doesn't have actionData
    // Query the action directly using the URL param
    logLoader('No sessionStorage data found, fetching action from params.id:', params.id);

    const {
      data: { action },
    } = await client.query({
      query: GET_ACTION_BY_ID,
      variables: { id: params.id },
    });

    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: action.orderIDs[0] },
    });

    actionData = {
      id: params.id,
      orderId: order.id,
      roid: order.roid,
      isActionEdited: 'false',
      origin: 'url-direct', // Añadido para consistencia con double-click
    };

    const parentOrder = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });
    parentExpeditionOrderStatus = parentOrder.data.order.state;
    state = order.state;
  }

  const {
    data: { concessions },
  } = await client.query({
    query: GET_CONCESSIONS,
  });

  logLoader('🔄 Llamando a loadFormOptions con actionId:', actionData.id);

  const loaderToResolve = (await loadFormOptions(
    userData,
    actionData.id
  )) as any;

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 DEBUG: SERVIDOR - Operarios y Driver (JSON para copiar)
  // ═══════════════════════════════════════════════════════════════════════════
  const serverInternalOps = loaderToResolve?.operatorsTable || [];
  const serverVehicles = loaderToResolve?.vehicleTable || {};

  // Extraer info simplificada de operarios
  const serverOpsSimple = serverInternalOps.map((op: any) => ({
    id: op?.id,
    name: op?.name,
    surName: op?.surName,
    internal: op?.internal,
    isActive: op?.isActive
  }));

  // Extraer info simplificada de vehículos con driver
  const serverVehiclesSimple = Object.entries(serverVehicles).map(([key, v]: [string, any]) => ({
    key,
    licensePlate: v?.licensePlate,
    driver: v?.driver ? { id: v.driver.id, name: v.driver.name } : null,
    driverId: v?.driverId
  }));

  logOpsDriver('════════════════════════════════════════════════════════════════');
  logOpsDriver('🔄 SERVIDOR - OPERARIOS:');
  logOpsDriver(JSON.stringify(serverOpsSimple, null, 2));
  logOpsDriver('════════════════════════════════════════════════════════════════');
  logOpsDriver('🔄 SERVIDOR - VEHÍCULOS CON DRIVER:');
  logOpsDriver(JSON.stringify(serverVehiclesSimple, null, 2));
  logOpsDriver('════════════════════════════════════════════════════════════════');

  logLoader(' Combinando datos de sessionStorage y servidor');
  logData(
    ' Datos de sessionStorage:',
    sessionStorageData?.externals?.length || 0
  );
  logData(' Datos del servidor:', loaderToResolve?.externals?.length || 0);

  // Empresas externas que el usuario MARCÓ para borrar (con workId = ya
  // guardadas). Ese delta solo se aplicaba al GUARDAR; sin restarlo aquí, el
  // union re-traía del servidor la empresa borrada → reaparecía al cambiar de
  // pestaña. `EXTERNAL_WORKS_TO_DELETE` se limpia en CADA entrada a la actuación,
  // así que no contamina entre actuaciones. (Las nuevas, sin workId, no estaban
  // en el servidor → su borrado ya persistía.)
  let worksToDelete: string[] = [];
  const worksToDeleteRaw = sessionStorage.getItem(
    externalCompaniesNaming.EXTERNAL_WORKS_TO_DELETE
  );
  if (worksToDeleteRaw) {
    try {
      worksToDelete = JSON.parse(decompressSessionStorage(worksToDeleteRaw));
    } catch {
      worksToDelete = [];
    }
  }
  const serverExternals = (loaderToResolve?.externals || []).filter(
    (e: { workId?: string }) => !e.workId || !worksToDelete.includes(e.workId)
  );

  // Combinamos sesión + servidor (menos los borrados) para no perder información
  const externals = [
    ...(sessionStorageData?.externals || []),
    ...serverExternals,
  ];

  logDuplicate(' Total de externals antes de deduplicar:', externals.length);
  if (externals.length > 0) {
    logDuplicate(' Lista detallada de externals antes de deduplicar:');
    externals.forEach((external, index) => {
      logDuplicate(
        `  [${index}] Empresa: ${external.company}, Operarios: ${
          external.operatorsNumber
        }, Horas: ${external.hours || 'N/A'}`
      );
    });
  }

  // Improved deduplication using Map with compound keys
  const uniqueExternalsMap = new Map();

  externals.forEach((external) => {
    // Create a compound key with all relevant attributes for better identification
    const uniqueKey = `${external.company}_${external.operatorsNumber}_${
      external.hours || ''
    }`;

    logDuplicate(` Procesando clave: ${uniqueKey}`);

    // Only keep the first external found for each unique key
    if (!uniqueExternalsMap.has(uniqueKey)) {
      uniqueExternalsMap.set(uniqueKey, external);
      logDuplicate(` Añadido como único: ${uniqueKey}`);
    } else {
      logDuplicate(` Duplicado detectado: ${uniqueKey}`);
    }
  });

  // Convert Map back to array
  const uniqueCompanies = Array.from(uniqueExternalsMap.values());

  logDuplicate(
    ' Total de externals después de deduplicar:',
    uniqueCompanies.length
  );
  if (uniqueCompanies.length > 0) {
    logDuplicate(' Lista detallada de externals después de deduplicar:');
    uniqueCompanies.forEach((external, index) => {
      logDuplicate(
        `  [${index}] Empresa: ${external.company}, Operarios: ${
          external.operatorsNumber
        }, Horas: ${external.hours || 'N/A'}`
      );
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔬 DEBUG - DIAGNÓSTICO DEL BUG DE OPERARIOS PERDIDOS
  // ═══════════════════════════════════════════════════════════════════════════
  // Contar operarios válidos (filtrar nulls/undefined)
  const serverOps = loaderToResolve?.operatorsTable?.filter((x: any) => x) || [];
  const sessionOps = sessionStorageData?.operatorsTable?.filter((x: any) => x) || [];
  const serverVehicleKeys = Object.keys(loaderToResolve?.vehicleTable || {});
  const sessionVehicleKeys = Object.keys(sessionStorageData?.vehicleTable || {});
  const bugDetected = serverOps.length > 0 && sessionOps.length === 0;

  logDebug('═══ ROIS DEBUG: ActionsValidation OperatorsAndVehicles Loader ═══');
  logDebug('SERVIDOR (loaderToResolve): %O', {
    operatorsTable_count: serverOps.length,
    operatorsTable: serverOps.map((op: any) => ({ id: op?.id, name: op?.operator?.name || op?.name })),
    vehicleTable_keys: serverVehicleKeys,
    hoursType: loaderToResolve?.hoursType,
  });
  logDebug('SESSION_STORAGE: %O', {
    operatorsTable_count: sessionOps.length,
    operatorsTable: sessionOps.map((op: any) => ({ id: op?.id, name: op?.operator?.name || op?.name })),
    vehicleTable_keys: sessionVehicleKeys,
    hoursType: sessionStorageData?.hoursType,
  });
  logDebug('ANÁLISIS: %O', {
    servidor_operarios: serverOps.length,
    session_operarios: sessionOps.length,
    servidor_vehiculos: serverVehicleKeys.length,
    session_vehiculos: sessionVehicleKeys.length,
    BUG_DETECTADO: bugDetected ? '⚠️ SÍ - Session vacía sobrescribirá servidor!' : 'NO',
  });
  logDebug('═══════════════════════════════════════════════════════════════════');
  // ═══════════════════════════════════════════════════════════════════════════

  // ✅ SOLUCIÓN QUIRÚRGICA (no simplista): Orden original + overrides específicos
  // Orden: server → session (session gana para preservar selecciones del usuario)
  // + Overrides específicos al final para campos donde el servidor DEBE ganar
  const combinedData = {
    // 1. Servidor primero (base de datos frescos)
    ...(typeof loaderToResolve === 'object' ? loaderToResolve : {}),

    // 2. Session después (preserva selecciones del usuario: operatorsTable, vehicleTable, etc.)
    ...sessionStorageData,

    // 3. ActionData siempre gana (datos específicos de la action actual)
    ...actionData,

    // 4. Campos que SIEMPRE necesitan valores específicos
    parentExpeditionOrderStatus,
    state,
    dataFromServer: loaderToResolve,
    externals: uniqueCompanies,
    concessions,

    // ✅ FIX BUG #21: Smart merge - usar servidor si session está vacía
    // CORREGIDO: Usar Array.isArray y some() para evaluación más robusta
    operatorsTable:
      Array.isArray(sessionStorageData?.operatorsTable) &&
      sessionStorageData.operatorsTable.some((x: any) => x != null)
        ? sessionStorageData.operatorsTable
        : loaderToResolve?.operatorsTable || [],

    vehicleTable:
      sessionStorageData?.vehicleTable &&
      typeof sessionStorageData.vehicleTable === 'object' &&
      Object.values(sessionStorageData.vehicleTable).some((v: any) => v != null)
        ? sessionStorageData.vehicleTable
        : loaderToResolve?.vehicleTable || {},

    // ✅ FIX: Catálogo siempre del servidor (incluye vehículos recién creados en Maquinaria)
    internalOperators: loaderToResolve?.internalOperators,

    // ✅ FIX: Extract teams from internalOperators to top level (component expects initialData.teams)
    // Note: internalOperators can be either an array [] or object { vehicles, operators, teams }
    // This enables Tablet role auto-filtering by team
    teams:
      typeof loaderToResolve === 'object' &&
      loaderToResolve?.internalOperators &&
      !Array.isArray(loaderToResolve.internalOperators)
        ? loaderToResolve.internalOperators.teams || []
        : [],
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🎯 DEBUG: FINAL - Datos que van al componente (JSON para copiar)
  // ═══════════════════════════════════════════════════════════════════════════
  const finalInternalOps = combinedData.operatorsTable || [];
  const finalVehicles = combinedData.vehicleTable || {};

  // Extraer info simplificada de operarios finales
  const finalOpsSimple = finalInternalOps.map((op: any) => ({
    id: op?.id,
    name: op?.name,
    surName: op?.surName,
    internal: op?.internal,
    isActive: op?.isActive
  }));

  // Extraer info simplificada de vehículos con driver
  const finalVehiclesSimple = Object.entries(finalVehicles).map(([key, v]: [string, any]) => ({
    key,
    licensePlate: v?.licensePlate,
    driver: v?.driver ? { id: v.driver.id, name: v.driver.name, surName: v.driver.surName } : null,
    driverId: v?.driverId
  }));

  logOpsDriver('════════════════════════════════════════════════════════════════');
  logOpsDriver('📤 FINAL - OPERARIOS que van al componente:');
  logOpsDriver(JSON.stringify(finalOpsSimple, null, 2));
  logOpsDriver('════════════════════════════════════════════════════════════════');
  logOpsDriver('📤 FINAL - VEHÍCULOS CON DRIVER que van al componente:');
  logOpsDriver(JSON.stringify(finalVehiclesSimple, null, 2));
  logOpsDriver('════════════════════════════════════════════════════════════════');
  logOpsDriver('🎯 RESUMEN:');
  logOpsDriver(JSON.stringify({
    operarios_count: finalOpsSimple.length,
    vehiculos_count: Object.keys(finalVehicles).length,
    tiene_operarios: finalOpsSimple.length > 0,
    tiene_vehiculos: Object.keys(finalVehicles).length > 0,
    algun_vehiculo_con_driver: finalVehiclesSimple.some(v => v.driver !== null)
  }, null, 2));
  logOpsDriver('════════════════════════════════════════════════════════════════');

  return combinedData;
};

  return { data: load() };
};
